//? Importing hooks 
import { useState, useCallback } from "react";

//? Importing react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//! Importing applicationStudentAPI
import { applicationStudentAPI } from "../applicationsStudents/applicationStudentAPI";

//? Importing types
import { type Pagination } from "../../../services/stores/types";

//? Importing types
import {
  type ApplicationStudentDetails,
  type CreateApplicationStudentInput,
  type GetApplicationStudentFullResponse,
} from "../applicationsStudents/types/types";


/**
 * Custom hook `useApplicationStudents` to manage operations related to ApplicationStudents
 * within the context of a specific application.
 *
 * @param {string} applicationId - ID of the application for context-specific operations.
 * @param {string} token - Authentication token for API requests.
 * @returns {Object} The hook returns applicationStudents data, loading state, error messages, and functions to interact with the API.
 */
export const useApplicationStudents = (applicationId: string, token: string) => {
  const queryClient = useQueryClient();

  // * States for search and pagination
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  // * Define the query key
  const queryKey = [
    "applicationStudents",
    applicationId,
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  // ? Fetch applicationStudents using react-query
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      applicationStudentAPI.getAll(
        applicationId,
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000, // * Cache for 5 minutes
    enabled: !!applicationId && !!token, // * Only enable the query if applicationId and token exist
  });

  // * Extract data and pagination
  const applicationStudents = data?.applicationStudents || [];
  const fetchedPagination = data?.pagination || pagination;

  // ! Handlers for search and pagination
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  // * Mutations to create, update, and delete applicationStudents
  const createApplicationStudentMutation = useMutation({
    mutationFn: (applicationStudentData: CreateApplicationStudentInput) =>
      applicationStudentAPI.create(applicationId, applicationStudentData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateApplicationStudentMutation = useMutation({
    mutationFn: ({
      id,
      student_id,
    }: {
      id: string;
      student_id: string;
    }) =>
      applicationStudentAPI.update(applicationId, id, { student_id }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteApplicationStudentMutation = useMutation({
    mutationFn: (id: string) =>
      applicationStudentAPI.delete(applicationId, id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // TODO: Fetch details of an applicationStudent by ID
  const getApplicationStudentById = useCallback(
    async (id: string): Promise<ApplicationStudentDetails | null> => {
      try {
        const applicationStudentDetails = await applicationStudentAPI.getById(
          applicationId,
          id,
          token
        );
        return applicationStudentDetails;
      } catch (err) {
        console.error("Error fetching application student by ID:", err);
        return null;
      }
    },
    [applicationId, token]
  );

  //* Fetch all application students with full details
  const getAllApplicationStudentsFull = useCallback(
    async (applicationId: string): Promise<GetApplicationStudentFullResponse | null> => {
      try {
        const applicationStudentDetails = await applicationStudentAPI.getAllFull(applicationId, token);
        return applicationStudentDetails;
      } catch (err) {
        console.error("Error fetching all application students:", err);
        return null;
      }
    },
    [token]
  );

  // * Return values and functions
  return {
    applicationStudents,
    loading: isLoading || isFetching,
    error: isError ? (error as Error).message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createApplicationStudent: createApplicationStudentMutation.mutate,
    updateApplicationStudent: updateApplicationStudentMutation.mutate,
    deleteApplicationStudent: deleteApplicationStudentMutation.mutate,
    getApplicationStudentById,
    getAllApplicationStudentsFull,
    changePage,
    isCreating: createApplicationStudentMutation.isPending,
    isUpdating: updateApplicationStudentMutation.isPending,
    isDeleting: deleteApplicationStudentMutation.isPending,
  };
};
